import React, { useState, useId, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ChevronLeft, Home, CheckCircle,
  Bed, Users, Bath, Building, Image as ImageIcon, UploadCloud,
  ArrowRight, Sparkles, ShieldCheck, X, Lock, MapPin, AlertCircle
} from 'lucide-react';
import axiosInstance from '../apis/axiosConfig';
import { compressImageFile } from '../utils/imageCompressor';
import logoImg from '../asserts/logo.png';
import '../pages_premium.css';

const STEPS = [
  { id: 1, title: 'What are you offering?', short: 'Offering', subtitle: 'Pick the type of listing that fits your space' },
  { id: 2, title: 'About your home', short: 'Your home', subtitle: 'Tell us a little about the whole property' },
  { id: 3, title: 'About your room', short: 'Your room', subtitle: 'Set up the details of the room for rent' },
  { id: 4, title: 'Rent & availability', short: 'Rent & bills', subtitle: 'Set your price, bills and move-in date' },
  { id: 5, title: 'Show off your place', short: 'Photos', subtitle: 'Photos make your listing 2x more popular' },
  { id: 6, title: 'Review & publish', short: 'Review', subtitle: 'Check everything and go live' },
];

const STEP_ICONS = [null, Home, Sparkles, Bed, Users, UploadCloud, ShieldCheck];

export default function AddProperty() {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [stepError, setStepError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [popupAlert, setPopupAlert] = useState({ open: false, title: '', message: '', subtitle: '' });

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [formData, setFormData] = useState({
    offerType: 'room',
    listing_type: 'Private room',
    propertyType: 'House',
    roomCount: '1',
    address: '',
    street: '',
    suburb: '',
    state: 'NSW',
    postcode: '',
    totalPeople: '2',
    totalBedrooms: '4',
    totalBathrooms: '3',
    roomType: 'Private',
    furnished: 'Furnished',
    furnishing_status: 'Furnished',
    furniture_items: ['Bed', 'Desk', 'Refrigerator', 'Wardrobe'],
    parking_spaces_count: '1',
    parking_type: 'Garage',
    bathroomAccess: 'Own',
    rent: '',
    bills_included_type: 'Included in Rent',
    billsIncluded: ['Electricity', 'Water', 'Internet'],
    estimated_bill_range: '$50-$100',
    billing_period: 'Monthly',
    availableDate: '',
    inspection_date: '',
    minStay: 'None',
    maxStay: 'None',
    bond_type: '4 weeks rent',
    bond_exact_amount: '',
    household_occupant_types: 'Students',
    occupant_count_range: '1-2',
    couples_accepted: false,
    students_accepted: true,
    pets_accepted: false,
    smoking_rule: 'Strictly No Smoking',
    visitors_allowed: 'Yes',
    cleaning_arrangement: 'Shared Rotation',
    images: [],
    rawImages: []
  });

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (formData.address.length > 0 && showSuggestions) {
        setIsSearchingAddress(true);
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(formData.address)}&countrycodes=au&format=json&addressdetails=1&limit=10`
          );
          const data = await res.json();
          setAddressSuggestions(Array.isArray(data) ? data : []);
        } catch (err) {
          console.error(err);
        } finally {
          setIsSearchingAddress(false);
        }
      } else {
        setAddressSuggestions([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [formData.address, showSuggestions]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const MAX_PHOTOS = 3;
  const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

  const handleImageChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const selectedFiles = Array.from(e.target.files);
    e.target.value = '';

    // 1. Check 10 MB size limit per image immediately
    const oversizedFiles = selectedFiles.filter(file => file.size > MAX_FILE_SIZE_BYTES);
    const sizeValidFiles = selectedFiles.filter(file => file.size <= MAX_FILE_SIZE_BYTES);

    const existingCount = (formData.rawImages || []).length;
    const remainingSlots = Math.max(0, MAX_PHOTOS - existingCount);

    if (oversizedFiles.length > 0) {
      const names = oversizedFiles.map(f => `${f.name} (${(f.size / (1024 * 1024)).toFixed(1)} MB)`).join(', ');
      setPopupAlert({
        open: true,
        title: 'Image Size Exceeds 10 MB!',
        message: `Each photo must be under 10 MB. The following photo(s) could not be added: ${names}`,
        subtitle: 'Maximum allowed file size is 10 MB per photo.'
      });
      if (sizeValidFiles.length === 0) return;
    }

    // 2. Check 3 photo limit immediately
    if (existingCount >= MAX_PHOTOS || sizeValidFiles.length > remainingSlots) {
      setPopupAlert({
        open: true,
        title: 'Only 3 Photos Allowed!',
        message: 'You can upload a maximum of 3 photos per listing. Please select up to 3 best photos of your place.',
        subtitle: 'Maximum 3 photos • Up to 10 MB each'
      });
    }

    const filesToAdd = sizeValidFiles.slice(0, remainingSlots);
    if (filesToAdd.length > 0) {
      Promise.all(filesToAdd.map(file => compressImageFile(file))).then(results => {
        const imagePreviews = results.map(r => r.previewUrl);
        const optimizedFiles = results.map(r => r.file);
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...imagePreviews].slice(0, MAX_PHOTOS),
          rawImages: [...(prev.rawImages || []), ...optimizedFiles].slice(0, MAX_PHOTOS)
        }));
      });
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== indexToRemove),
      rawImages: (prev.rawImages || []).filter((_, i) => i !== indexToRemove)
    }));
  };

  const handleNext = () => {
    setStepError('');
    if (currentStep === 1) {
      if (!formData.offerType) {
        setStepError('Please select what you are offering (Room or Whole property).');
        return;
      }
      if (!formData.propertyType) {
        setStepError('Please select a property type.');
        return;
      }
    } else if (currentStep === 2) {
      if (!formData.address?.trim() && !formData.suburb?.trim() && !formData.street?.trim()) {
        setStepError('Please enter property address or suburb before proceeding.');
        return;
      }
      if (!formData.totalBedrooms) {
        setStepError('Please specify the number of bedrooms.');
        return;
      }
      if (!formData.totalBathrooms) {
        setStepError('Please specify the number of bathrooms.');
        return;
      }
    } else if (currentStep === 3) {
      if (formData.offerType === 'room' && !formData.roomType) {
        setStepError('Please select a room type (e.g. Private room, Shared room).');
        return;
      }
    } else if (currentStep === 4) {
      const cleanedRent = parseFloat(String(formData.rent).replace(/[^0-9.]/g, ''));
      if (!cleanedRent || cleanedRent <= 0) {
        setStepError('Please enter a valid weekly rent amount in Dollars ($).');
        return;
      }
      if (!formData.availableDate) {
        setStepError('Please select an available move-in date.');
        return;
      }
    } else if (currentStep === 5) {
      if (!formData.images || formData.images.length === 0) {
        setStepError('Please upload at least one photo of the property.');
        return;
      }
      if (formData.images.length > MAX_PHOTOS) {
        setPopupAlert({
          open: true,
          title: 'Only 3 Photos Allowed!',
          message: 'Please keep a maximum of 3 photos before continuing.',
          subtitle: 'Maximum 3 photos • Up to 10 MB each'
        });
        return;
      }
    }
    setCurrentStep(p => Math.min(p + 1, 6));
  };

  const handlePrev = () => {
    setStepError('');
    if (currentStep <= 1) {
      navigate('/properties');
      return;
    }
    setCurrentStep(p => Math.max(p - 1, 1));
  };

  const handlePublish = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const data = new FormData();
      const parts = (formData.address || '').split(',').map(s => s.trim());
      const street = formData.street || parts[0] || formData.address || 'Unknown Street';
      const suburb = formData.suburb || parts[1] || 'Sydney';
      const state = formData.state || (parts[2] ? parts[2].split(' ')[0] : 'NSW');
      const postcode = formData.postcode || '';

      const fullAddressString = formData.address || `${street}, ${suburb} ${state} ${postcode}`.trim();

      data.append('listing_role', 'admin');
      data.append('title', `${formData.totalBedrooms || 1} Bed ${formData.offerType === 'room' ? 'Share House' : 'Property'} in ${suburb}`);
      data.append('offerType', formData.offerType || 'room');
      data.append('address', fullAddressString);

      // Append top-level structured fields for Property.create
      data.append('address_street', street);
      data.append('address_suburb', suburb);
      data.append('address_state', state);
      data.append('address_postcode', postcode);

      const cleanRentNum = parseFloat(String(formData.rent || '0').replace(/[^0-9.]/g, '')) || 0;
      const cleanBondRaw = String(formData.bond_exact_amount || '').replace(/[^0-9.]/g, '');
      const cleanBondNum = cleanBondRaw ? parseFloat(cleanBondRaw) : (cleanRentNum > 0 ? cleanRentNum * 4 : '');

      const rawBillRange = (formData.estimated_bill_range || '').trim();
      const formattedBillRange = rawBillRange && !rawBillRange.includes('$') ? `$${rawBillRange}` : (rawBillRange || '$50-$100');

      data.append('listing_type', formData.listing_type || (formData.offerType === 'whole' ? 'Entire property' : 'Private room'));
      data.append('property_type', formData.propertyType || 'House');
      data.append('bills_included_type', formData.bills_included_type);
      data.append('bills_included_items', JSON.stringify(formData.billsIncluded || []));
      data.append('estimated_bill_range', formattedBillRange);
      data.append('billing_period', formData.billing_period);
      data.append('furnishing_status', formData.furnishing_status || formData.furnished);
      data.append('furniture_items', JSON.stringify(formData.furniture_items || []));
      data.append('parking_spaces_count', formData.parking_spaces_count);
      data.append('parking_type', formData.parking_type);
      data.append('bond_type', formData.bond_type);
      if (cleanBondNum !== '' && Number.isFinite(cleanBondNum)) {
        data.append('bond_exact_amount', String(cleanBondNum));
      }
      data.append('household_occupant_types', formData.household_occupant_types);
      data.append('occupant_count_range', formData.occupant_count_range);
      data.append('couples_accepted', formData.couples_accepted ? 'true' : 'false');
      data.append('students_accepted', formData.students_accepted ? 'true' : 'false');
      data.append('pets_accepted', formData.pets_accepted ? 'true' : 'false');
      data.append('smoking_rule', formData.smoking_rule);
      data.append('visitors_allowed', formData.visitors_allowed);
      data.append('cleaning_arrangement', formData.cleaning_arrangement);

      const aboutHome = {
        propertyType: formData.propertyType,
        roomCount: formData.roomCount,
        totalPeople: formData.totalPeople,
        totalBedrooms: formData.totalBedrooms,
        totalBathrooms: formData.totalBathrooms
      };
      data.append('about_home', JSON.stringify(aboutHome));

      const aboutRoom = {
        roomType: formData.roomType,
        furnished: formData.furnished,
        bathroomAccess: formData.bathroomAccess
      };
      data.append('about_room', JSON.stringify(aboutRoom));

      data.append('rent', String(cleanRentNum));

      const rentAndAvailability = {
        billsIncluded: formData.billsIncluded
      };
      data.append('rent_and_availability', JSON.stringify(rentAndAvailability));

      data.append('availableDate', formData.availableDate || new Date().toISOString().slice(0, 10));
      data.append('inspection_date', formData.inspection_date || '');
      data.append('minStay', formData.minStay);
      data.append('maxStay', formData.maxStay);

      if (formData.rawImages && formData.rawImages.length > 0) {
        formData.rawImages.slice(0, MAX_PHOTOS).forEach(file => {
          data.append('images', file);
        });
      }

      await axiosInstance.post('/properties', data);

      toast.success('Property created successfully!');
      navigate('/properties');
    } catch (err) {
      console.error('Submit property error:', err);
      const errMsg = err.response?.data?.message || err.message || 'Failed to publish listing.';
      setPopupAlert({
        open: true,
        title: 'Could Not Publish Listing',
        message: errMsg,
        subtitle: 'Please verify your details and try again.'
      });
      setIsSubmitting(false);
    }
  };

  const progress = Math.round((currentStep / 6) * 100);
  const currentMeta = STEPS[currentStep - 1] || STEPS[0];

  return (
    <div className="admin-add-property-wizard min-h-[calc(100vh-110px)] bg-gradient-to-b from-[#0f1017] via-[#0f1017] to-[#1a0f14] rounded-2xl sm:rounded-3xl flex flex-col relative overflow-hidden shadow-2xl border border-white/10 mb-6">
      <style>{`
        .admin-add-property-wizard,
        .admin-add-property-wizard *,
        .admin-add-property-wizard *::before,
        .admin-add-property-wizard *::after {
          box-sizing: border-box;
          border-width: 0;
          border-style: solid;
          border-color: rgba(255, 255, 255, 0.1);
        }
        .admin-add-property-wizard h1,
        .admin-add-property-wizard h2,
        .admin-add-property-wizard h3,
        .admin-add-property-wizard h4,
        .admin-add-property-wizard h5,
        .admin-add-property-wizard h6,
        .admin-add-property-wizard p,
        .admin-add-property-wizard label {
          margin: 0;
        }
        .admin-add-property-wizard button {
          background-color: transparent;
          background-image: none;
          cursor: pointer;
          font-family: inherit;
        }
        .admin-add-property-wizard input,
        .admin-add-property-wizard select,
        .admin-add-property-wizard textarea {
          font-family: inherit;
        }
        .admin-add-property-wizard .border { border-width: 1px !important; }
        .admin-add-property-wizard .border-2 { border-width: 2px !important; }
        .admin-add-property-wizard .border-t { border-top-width: 1px !important; }
        .admin-add-property-wizard .border-b { border-bottom-width: 1px !important; }
        .admin-add-property-wizard .border-dashed { border-style: dashed !important; }
      `}</style>

      {/* Ambient background glows */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[45%] h-[45%] rounded-full bg-red-600/15 blur-[160px]" />
        <div className="absolute top-1/3 -right-40 w-[40%] h-[40%] rounded-full bg-indigo-600/10 blur-[150px]" />
        <div className="absolute bottom-0 left-1/4 w-[35%] h-[35%] rounded-full bg-red-900/10 blur-[140px]" />
      </div>

      {/* Header */}
      <header className="relative z-20 flex justify-between items-center py-3.5 sm:py-4 px-4 sm:px-6 md:px-8 border-b border-white/10 bg-[#0f1017]/60 backdrop-blur-md gap-3">
        <div className="flex items-center gap-2.5 shrink-0">
          <img src={logoImg} alt="Students Union" className="h-7 sm:h-9 w-auto object-contain" />
          <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-red-400 bg-red-500/10 px-2.5 py-0.5 rounded-full border border-red-500/20 whitespace-nowrap">
            List Space
          </span>
        </div>

        {/* Centered Stepper on Desktop */}
        <div className="hidden xl:flex items-center w-full max-w-2xl mx-auto justify-between gap-0">
          {STEPS.map((step, i) => {
            const stepNum = i + 1;
            const isDone = stepNum < currentStep;
            const isActive = stepNum === currentStep;
            return (
              <div key={step.id} className="flex items-center flex-1 last:flex-none">
                <button
                  type="button"
                  onClick={() => {
                    if (stepNum < currentStep) {
                      setStepError('');
                      setCurrentStep(stepNum);
                    }
                  }}
                  className={`flex items-center gap-2 pr-2 rounded-full ${stepNum < currentStep ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black transition-all duration-300 shrink-0 ${
                      isActive
                        ? 'bg-red-500 text-white scale-110 shadow-lg shadow-red-900/40'
                        : isDone
                        ? 'bg-emerald-500 text-white'
                        : 'bg-white/10 text-gray-400'
                    }`}
                  >
                    {isDone ? <CheckCircle size={13} /> : stepNum}
                  </span>
                  <span
                    className={`text-xs font-semibold whitespace-nowrap transition-colors ${
                      isActive ? 'text-white' : isDone ? 'text-emerald-300' : 'text-gray-500'
                    }`}
                  >
                    {step.short}
                  </span>
                </button>
                {stepNum < 6 && (
                  <div className={`flex-1 h-px min-w-[12px] mx-2 ${stepNum <= currentStep ? 'bg-emerald-500/50' : 'bg-white/10'}`} />
                )}
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => navigate('/properties')}
          title="Close and return to Property List"
          className="text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 backdrop-blur-md p-2 sm:p-2.5 rounded-full transition-all border border-white/10 shrink-0"
        >
          <X size={18} />
        </button>
      </header>

      {/* Main Content Area */}
      <div className="relative z-10 flex-1 flex flex-col px-3.5 sm:px-6 md:px-8 pb-8 pt-4 sm:pt-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 20, scale: 0.99 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -15, scale: 0.99 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-2xl mx-auto flex-1 flex flex-col"
          >
            {/* Step Header */}
            <div className="text-center mb-4 sm:mb-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1, type: 'spring', stiffness: 300, damping: 18 }}
                className="inline-flex w-11 h-11 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 text-white items-center justify-center shadow-xl shadow-red-900/40 ring-4 ring-red-500/20 mb-3 sm:mb-4"
              >
                {(() => {
                  const Icon = STEP_ICONS[currentStep] || Sparkles;
                  return <Icon className="w-5 h-5 sm:w-6 sm:h-6" />;
                })()}
              </motion.div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight leading-tight">
                {currentMeta.title}
              </h2>
              <p className="text-gray-400 mt-1.5 sm:mt-2 text-xs sm:text-sm md:text-base px-2">{currentMeta.subtitle}</p>

              {/* Progress bar on screens below xl */}
              <div className="xl:hidden max-w-xs mx-auto mt-3.5 sm:mt-5">
                <div className="flex justify-between text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
                  <span>Step {currentStep} of 6</span>
                  <span className="text-red-400 font-extrabold">{progress}%</span>
                </div>
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-full"
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  />
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-9 shadow-2xl shadow-black/30">
              {/* Step 1: What are you offering */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <OfferCard
                      selected={formData.offerType === 'room'}
                      onSelect={() => {
                        handleChange('offerType', 'room');
                        handleChange('listing_type', 'Private room');
                      }}
                      icon={<Bed size={26} />}
                      title="Room in a share house"
                      desc="You have one or more bedrooms to fill in an existing household."
                    />
                    <OfferCard
                      selected={formData.offerType === 'whole'}
                      onSelect={() => {
                        handleChange('offerType', 'whole');
                        handleChange('listing_type', 'Entire property');
                      }}
                      icon={<Home size={26} />}
                      title="Whole property for rent"
                      desc="The entire home is unoccupied and available to rent."
                    />
                  </div>

                  <div className="border-t border-white/10 pt-5 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Listing Type *</label>
                        <select
                          value={formData.listing_type}
                          onChange={(e) => handleChange('listing_type', e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-red-500 cursor-pointer"
                        >
                          {['Entire property', 'Private room', 'Shared room', 'Temporary accommodation', 'Lease transfer', 'Looking for a flatmate'].map(opt => (
                            <option key={opt} value={opt} className="bg-gray-900 text-white">{opt}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Property Type *</label>
                        <select
                          value={formData.propertyType}
                          onChange={(e) => handleChange('propertyType', e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-red-500 cursor-pointer"
                        >
                          {['House', 'Apartment', 'Unit', 'Townhouse', 'Studio', 'Student accommodation'].map(opt => (
                            <option key={opt} value={opt} className="bg-gray-900 text-white">{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Bond Type</label>
                        <select
                          value={formData.bond_type}
                          onChange={(e) => handleChange('bond_type', e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-red-500 cursor-pointer"
                        >
                          {['4 weeks rent', '2 weeks rent', 'Custom / None'].map(opt => (
                            <option key={opt} value={opt} className="bg-gray-900 text-white">{opt}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Exact Bond Amount (AUD $)</label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
                          <input
                            type="number"
                            min="0"
                            value={formData.bond_exact_amount}
                            onChange={(e) => handleChange('bond_exact_amount', e.target.value)}
                            placeholder="1800"
                            className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-3 text-sm text-white placeholder-gray-500 outline-none focus:border-red-500 font-medium"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: About your home */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div className="bg-indigo-500/10 border border-indigo-500/20 text-indigo-200 p-4 rounded-xl flex gap-3 text-sm">
                    <Lock size={18} className="shrink-0 mt-0.5 text-indigo-300" />
                    <div>
                      <span className="font-bold block mb-0.5">🔒 Address Privacy Active</span>
                      <span className="opacity-80">
                        Full street address is hidden publicly. Only the Suburb and approximate map location appear until inspection is confirmed!
                      </span>
                    </div>
                  </div>

                  <div className="relative">
                    <label className="text-sm font-bold text-white mb-2 block">Search Full Address</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => {
                        handleChange('address', e.target.value);
                        setShowSuggestions(true);
                      }}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      onFocus={() => setShowSuggestions(true)}
                      placeholder="e.g. 345 Queen St, Brisbane City QLD 4000"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-500 focus:border-red-500 focus:ring-4 focus:ring-red-500/10 transition-all outline-none"
                    />

                    {/* Autocomplete Dropdown */}
                    {showSuggestions && formData.address.length > 0 && (
                      <div
                        className="absolute z-50 w-full mt-2 bg-[#1a1c23] border border-white/10 rounded-xl shadow-2xl shadow-black/50 overflow-hidden"
                        style={{ maxHeight: '250px', overflowY: 'auto' }}
                      >
                        {isSearchingAddress ? (
                          <div className="p-4 text-gray-400 text-sm">Searching locations...</div>
                        ) : addressSuggestions.length > 0 ? (
                          addressSuggestions.map((place, idx) => (
                            <div
                              key={idx}
                              className="p-3 border-b border-white/5 hover:bg-white/5 cursor-pointer flex items-start gap-3 transition-colors"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleChange('address', place.display_name);
                                const parts = place.display_name.split(',').map(s => s.trim());
                                if (parts[0]) handleChange('street', parts[0]);
                                if (parts[1]) handleChange('suburb', parts[1]);
                                setShowSuggestions(false);
                              }}
                            >
                              <MapPin size={18} className="text-red-400 shrink-0 mt-0.5" />
                              <div>
                                <strong className="text-white text-sm block">{place.display_name.split(',')[0]}</strong>
                                <span className="text-xs text-gray-400">
                                  {place.display_name.substring(place.display_name.indexOf(',') + 1)}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-4 text-gray-400 text-sm">No Australian locations found.</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-white/10 pt-4">
                    <div>
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Street Address *</label>
                      <input
                        type="text"
                        value={formData.street}
                        onChange={(e) => handleChange('street', e.target.value)}
                        placeholder="e.g. 123 Main Street"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 outline-none focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Suburb / City *</label>
                      <input
                        type="text"
                        value={formData.suburb}
                        onChange={(e) => handleChange('suburb', e.target.value)}
                        placeholder="e.g. Sydney"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 outline-none focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">State *</label>
                      <select
                        value={formData.state}
                        onChange={(e) => handleChange('state', e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-red-500 cursor-pointer"
                      >
                        {['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT'].map(s => (
                          <option key={s} value={s} className="bg-gray-900 text-white">{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-bold text-white mb-3 block">How many people will live in your home?</label>
                    <SegmentedControl
                      options={['1', '2', '3', '4+']}
                      value={formData.totalPeople}
                      onChange={(v) => handleChange('totalPeople', v)}
                    />
                    <p className="text-xs text-gray-400 mt-2">Including yourself</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-bold text-white mb-3 block">Bedrooms</label>
                      <SegmentedControl
                        options={['1', '2', '3', '4', '5+']}
                        value={formData.totalBedrooms}
                        onChange={(v) => handleChange('totalBedrooms', v)}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-white mb-3 block">Bathrooms</label>
                      <SegmentedControl
                        options={['1', '2', '3', '4+']}
                        value={formData.totalBathrooms}
                        onChange={(v) => handleChange('totalBathrooms', v)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: About your room */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <label className="text-sm font-bold text-white mb-3 block">Is the room private or shared?</label>
                    <SegmentedControl
                      options={['Private', 'Shared']}
                      value={formData.roomType}
                      onChange={(v) => handleChange('roomType', v)}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Furnishing Status</label>
                      <select
                        value={formData.furnishing_status}
                        onChange={(e) => {
                          handleChange('furnishing_status', e.target.value);
                          handleChange('furnished', e.target.value);
                        }}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-red-500 cursor-pointer"
                      >
                        {['Furnished', 'Unfurnished', 'Partially Furnished'].map(opt => (
                          <option key={opt} value={opt} className="bg-gray-900 text-white">{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Bathroom access</label>
                      <select
                        value={formData.bathroomAccess}
                        onChange={(e) => handleChange('bathroomAccess', e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-red-500 cursor-pointer"
                      >
                        {['Shared', 'Own', 'Ensuite'].map(opt => (
                          <option key={opt} value={opt} className="bg-gray-900 text-white">{opt}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Included Furniture Items Checkboxes */}
                  <div className="bg-white/5 border border-white/10 p-4 rounded-2xl">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 block">Included Furniture Items</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                      {['Bed', 'Desk', 'Refrigerator', 'Wardrobe', 'Sofa', 'Dining Table', 'Washing Machine', 'TV'].map(item => {
                        const active = formData.furniture_items.includes(item);
                        return (
                          <button
                            key={item}
                            type="button"
                            onClick={() => {
                              if (active) handleChange('furniture_items', formData.furniture_items.filter(i => i !== item));
                              else handleChange('furniture_items', [...formData.furniture_items, item]);
                            }}
                            className={`flex items-center gap-2 p-2.5 rounded-xl border text-xs font-bold transition-all ${
                              active ? 'bg-red-500/20 border-red-500 text-white' : 'bg-white/5 border-white/10 text-gray-400 hover:text-white'
                            }`}
                          >
                            <span
                              className={`w-3.5 h-3.5 rounded flex items-center justify-center text-[10px] shrink-0 ${
                                active ? 'bg-red-500 text-white' : 'border border-gray-500'
                              }`}
                            >
                              {active && '✓'}
                            </span>
                            <span className="truncate">{item}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Parking Spaces & Type */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Parking Spaces Count</label>
                      <input
                        type="number"
                        value={formData.parking_spaces_count}
                        onChange={(e) => handleChange('parking_spaces_count', e.target.value)}
                        placeholder="1"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 outline-none focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Parking Type</label>
                      <select
                        value={formData.parking_type}
                        onChange={(e) => handleChange('parking_type', e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-red-500 cursor-pointer"
                      >
                        {['Garage', 'Carport', 'Off-street', 'On-street', 'Dedicated space', 'None'].map(opt => (
                          <option key={opt} value={opt} className="bg-gray-900 text-white">{opt}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Rent & availability */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <div>
                    <label className="text-sm font-bold text-white mb-2 block">Weekly rent (AUD $)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
                      <input
                        type="number"
                        min="0"
                        value={formData.rent}
                        onChange={(e) => handleChange('rent', e.target.value)}
                        placeholder="450"
                        className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-3.5 text-white placeholder-gray-500 focus:border-red-500 focus:ring-4 focus:ring-red-500/10 transition-all outline-none font-medium"
                      />
                    </div>
                  </div>

                  {/* Bills Breakdown */}
                  <div className="bg-white/5 border border-white/10 p-4 rounded-2xl space-y-4">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Bills Breakdown</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Bills Included Type</label>
                        <select
                          value={formData.bills_included_type}
                          onChange={(e) => handleChange('bills_included_type', e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-red-500 cursor-pointer"
                        >
                          {['Included in Rent', 'Extra / Not Included', 'Partially Included'].map(opt => (
                            <option key={opt} value={opt} className="bg-gray-900 text-white">{opt}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Estimated Bill Range (AUD $)</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-xs">$</span>
                          <input
                            type="text"
                            value={String(formData.estimated_bill_range || '').replace(/^\$/, '')}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/^\$/, '');
                              handleChange('estimated_bill_range', raw ? `$${raw}` : '');
                            }}
                            placeholder="50-$100"
                            className="w-full bg-white/5 border border-white/10 rounded-xl pl-7 pr-3 py-2.5 text-xs text-white placeholder-gray-500 outline-none focus:border-red-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Billing Period</label>
                        <select
                          value={formData.billing_period}
                          onChange={(e) => handleChange('billing_period', e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-red-500 cursor-pointer"
                        >
                          {['Monthly', 'Weekly', 'Quarterly'].map(opt => (
                            <option key={opt} value={opt} className="bg-gray-900 text-white">{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-bold text-white mb-2 block">Available Date</label>
                      <input
                        type="date"
                        value={formData.availableDate}
                        onChange={(e) => handleChange('availableDate', e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white [color-scheme:dark] focus:border-red-500 transition-all outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-white mb-2 block">Inspection Date</label>
                      <input
                        type="date"
                        value={formData.inspection_date}
                        onChange={(e) => handleChange('inspection_date', e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white [color-scheme:dark] focus:border-red-500 transition-all outline-none"
                      />
                    </div>
                  </div>

                  {/* Household Occupants & House Rules */}
                  <div className="bg-white/5 border border-white/10 p-4 rounded-2xl space-y-4">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Occupants & House Rules</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Occupant Count Range</label>
                        <select
                          value={formData.occupant_count_range}
                          onChange={(e) => handleChange('occupant_count_range', e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs text-white outline-none cursor-pointer"
                        >
                          {['1-2', '3-4', '5+'].map(opt => (
                            <option key={opt} value={opt} className="bg-gray-900 text-white">{opt}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Occupants Type</label>
                        <select
                          value={formData.household_occupant_types}
                          onChange={(e) => handleChange('household_occupant_types', e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs text-white outline-none cursor-pointer"
                        >
                          {['Students', 'Young Professionals', 'Families', 'Mixed'].map(opt => (
                            <option key={opt} value={opt} className="bg-gray-900 text-white">{opt}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Smoking Rule</label>
                        <select
                          value={formData.smoking_rule}
                          onChange={(e) => handleChange('smoking_rule', e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs text-white outline-none cursor-pointer"
                        >
                          {['Strictly No Smoking', 'Outdoors Only', 'Allowed'].map(opt => (
                            <option key={opt} value={opt} className="bg-gray-900 text-white">{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3 pt-2">
                      <label className="flex items-center gap-2 text-xs text-white cursor-pointer font-bold bg-white/5 px-3 py-2 rounded-xl border border-white/10">
                        <input
                          type="checkbox"
                          checked={formData.couples_accepted}
                          onChange={(e) => handleChange('couples_accepted', e.target.checked)}
                        />
                        Couples Accepted
                      </label>
                      <label className="flex items-center gap-2 text-xs text-white cursor-pointer font-bold bg-white/5 px-3 py-2 rounded-xl border border-white/10">
                        <input
                          type="checkbox"
                          checked={formData.students_accepted}
                          onChange={(e) => handleChange('students_accepted', e.target.checked)}
                        />
                        Students Accepted
                      </label>
                      <label className="flex items-center gap-2 text-xs text-white cursor-pointer font-bold bg-white/5 px-3 py-2 rounded-xl border border-white/10">
                        <input
                          type="checkbox"
                          checked={formData.pets_accepted}
                          onChange={(e) => handleChange('pets_accepted', e.target.checked)}
                        />
                        Pets Accepted
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: Show off your place */}
              {currentStep === 5 && (
                <div>
                  <div className="bg-amber-500/10 border border-amber-500/20 text-amber-200 p-4 rounded-xl flex gap-3 text-sm font-medium items-center justify-between mb-6 flex-wrap">
                    <div className="flex items-center gap-3">
                      <Sparkles size={18} className="shrink-0 text-amber-300" />
                      <span>
                        Upload up to <strong className="text-white">3 clear photos</strong> of your place (Max <strong className="text-white">10 MB</strong> each)
                      </span>
                    </div>
                    <span className="text-xs font-black bg-white/10 text-white px-3 py-1 rounded-full">
                      {formData.images.length} / 3 Photos
                    </span>
                  </div>

                  <motion.div
                    whileHover={{ y: -2 }}
                    className="bg-white/5 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center min-h-[260px] text-center mb-4 border-2 border-dashed border-white/15 hover:border-red-500/50 transition-colors"
                  >
                    {formData.images.length === 0 ? (
                      <>
                        <motion.div
                          animate={{ y: [0, -8, 0] }}
                          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                          className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white shadow-xl shadow-red-900/40 mb-5"
                        >
                          <UploadCloud size={28} />
                        </motion.div>
                        <h3 className="text-lg sm:text-xl font-bold text-white mb-2">You haven't uploaded any photos yet!</h3>
                        <p className="text-gray-400 text-sm mb-6 max-w-xs">
                          Show off your place and let potential students know you're for real.
                        </p>
                        <label className="bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white font-bold py-3.5 px-8 rounded-xl cursor-pointer shadow-xl shadow-red-900/40 transition-all hover:-translate-y-0.5">
                          Add photos
                          <input
                            type="file"
                            multiple
                            accept="image/*,.heic,.heif,.avif,.jfif,.webp,.bmp,.svg,.tiff"
                            className="hidden"
                            onChange={handleImageChange}
                          />
                        </label>
                        <p className="text-xs text-gray-400 mt-3 font-semibold">Maximum 3 photos allowed • Max 10 MB per photo</p>
                      </>
                    ) : (
                      <div className="w-full">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 w-full mb-5">
                          {formData.images.map((imgUrl, idx) => (
                            <motion.div
                              key={idx}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              className="relative aspect-square rounded-xl overflow-hidden border border-white/10 group"
                            >
                              <img src={imgUrl} alt="Preview" className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={() => handleRemoveImage(idx)}
                                className="absolute top-2 right-2 bg-black/70 hover:bg-red-600 backdrop-blur-sm rounded-full p-1.5 text-white transition-all shadow-md"
                                title="Remove photo"
                              >
                                <X size={14} />
                              </button>
                            </motion.div>
                          ))}
                        </div>
                        {formData.images.length < 3 ? (
                          <label className="text-red-400 font-semibold cursor-pointer hover:text-red-300 transition-colors text-sm inline-block">
                            + Add more photos ({3 - formData.images.length} remaining)
                            <input
                              type="file"
                              multiple
                              accept="image/*,.heic,.heif,.avif,.jfif,.webp,.bmp,.svg,.tiff"
                              className="hidden"
                              onChange={handleImageChange}
                            />
                          </label>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              setPopupAlert({
                                open: true,
                                title: 'Only 3 Photos Allowed!',
                                message: 'You have already uploaded the maximum of 3 photos. Remove a photo first if you wish to replace it.',
                                subtitle: 'Maximum 3 photos • Up to 10 MB each'
                              })
                            }
                            className="text-amber-400 font-semibold hover:text-amber-300 transition-colors text-xs"
                          >
                            Maximum 3 photos reached (Click X on any photo to replace)
                          </button>
                        )}
                      </div>
                    )}
                  </motion.div>

                  <p className="text-xs text-gray-500 text-center">
                    For your safety, avoid photos showing the front of your house or street number.
                  </p>
                </div>
              )}

              {/* Step 6: Review & publish */}
              {currentStep === 6 && (
                <div className="space-y-8">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="relative w-full h-64 md:h-80 rounded-[2rem] overflow-hidden shadow-2xl group border border-white/10"
                  >
                    {formData.images && formData.images.length > 0 ? (
                      <img
                        src={formData.images[0]}
                        alt="Property Preview"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-white/5 to-white/10 flex flex-col items-center justify-center text-gray-500">
                        <ImageIcon size={48} className="mb-4 opacity-30" />
                        <p className="font-bold">No images uploaded</p>
                      </div>
                    )}

                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0f1017] via-[#0f1017]/50 to-transparent" />

                    {/* Floating Info inside Image */}
                    <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 md:p-8 text-white">
                      <div className="flex flex-wrap items-end justify-between gap-4">
                        <div className="flex-1 min-w-[200px]">
                          <div className="flex items-center gap-2 mb-2.5">
                            <span className="bg-red-600 px-3.5 py-1 rounded-full text-[11px] font-black tracking-widest uppercase shadow-lg shadow-red-600/30">
                              {formData.offerType === 'whole' ? 'Whole Property' : 'Share House'}
                            </span>
                          </div>
                          <h3 className="text-xl sm:text-2xl md:text-4xl font-black text-white drop-shadow-md mb-1.5">
                            {formData.totalBedrooms} Bed {formData.offerType === 'whole' ? 'Property' : 'Share House'}
                          </h3>
                          <p className="text-white/80 text-xs sm:text-sm font-medium flex items-center gap-2">
                            <Home size={16} className="text-red-400 shrink-0" />
                            <span className="truncate">{formData.address || `${formData.street || ''} ${formData.suburb || ''}`.trim() || 'Address not provided'}</span>
                          </p>
                        </div>
                        <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-3.5 sm:p-4 md:px-6 rounded-2xl text-center shadow-2xl shrink-0">
                          <p className="text-[10px] text-white/60 font-black uppercase tracking-widest mb-1">Weekly Rent (AUD)</p>
                          <p className="text-2xl sm:text-3xl md:text-4xl font-black text-white">
                            ${String(formData.rent || '0').replace(/^\$/, '')}
                          </p>
                          <p className="text-xs font-bold text-red-400 mt-1">
                            {formData.bills_included_type === 'Included in Rent' ? '+ Bills Included' : formData.bills_included_type}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                    {/* Property Specs */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                      className="bg-white/5 border border-white/10 shadow-lg rounded-3xl p-5 sm:p-6 md:p-8 flex flex-col justify-between h-full"
                    >
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 rounded-2xl bg-white/5 text-white flex items-center justify-center border border-white/10 shadow-inner shrink-0">
                          <Home size={24} />
                        </div>
                        <div>
                          <h4 className="text-lg font-black text-white">About the Home</h4>
                          <p className="text-xs font-bold text-gray-400">Core details of the property</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3.5">
                        <div className="bg-white/5 p-3.5 rounded-2xl border border-white/5 flex flex-col items-center justify-center text-center">
                          <Bed size={22} className="text-red-400 mb-1.5" />
                          <p className="text-xl font-black text-white">{formData.totalBedrooms}</p>
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mt-0.5">Total Beds</p>
                        </div>
                        <div className="bg-white/5 p-3.5 rounded-2xl border border-white/5 flex flex-col items-center justify-center text-center">
                          <Users size={22} className="text-red-400 mb-1.5" />
                          <p className="text-xl font-black text-white">{formData.totalPeople}</p>
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mt-0.5">Total People</p>
                        </div>
                        <div className="bg-white/5 p-3.5 rounded-2xl border border-white/5 flex flex-col items-center justify-center text-center">
                          <Bath size={22} className="text-red-400 mb-1.5" />
                          <p className="text-xl font-black text-white">{formData.totalBathrooms}</p>
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mt-0.5">Bathrooms</p>
                        </div>
                        <div className="bg-white/5 p-3.5 rounded-2xl border border-white/5 flex flex-col items-center justify-center text-center">
                          <Building size={22} className="text-red-400 mb-1.5" />
                          <p className="text-base font-black text-white truncate max-w-full" title={formData.propertyType || 'N/A'}>
                            {formData.propertyType || 'N/A'}
                          </p>
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mt-0.5">Type</p>
                        </div>
                      </div>
                    </motion.div>

                    {/* Room & Rent Specs */}
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      className="bg-white/5 border border-white/10 shadow-lg rounded-3xl p-5 sm:p-6 md:p-8 flex flex-col justify-between h-full"
                    >
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 rounded-2xl bg-white/5 text-white flex items-center justify-center border border-white/10 shadow-inner shrink-0">
                          <ShieldCheck size={24} />
                        </div>
                        <div>
                          <h4 className="text-lg font-black text-white">Room & Stay</h4>
                          <p className="text-xs font-bold text-gray-400">Details about the specific room</p>
                        </div>
                      </div>

                      <div className="space-y-3 flex-1 flex flex-col justify-between">
                        <div className="flex justify-between items-center gap-3 p-3.5 bg-white/5 rounded-2xl border border-white/5 min-w-0">
                          <span className="text-sm font-bold text-gray-400 shrink-0">Room Type</span>
                          <span className="text-xs md:text-sm font-black text-white bg-white/10 border border-white/10 px-3.5 py-1.5 rounded-xl truncate text-right max-w-[60%]">
                            {formData.roomType || 'Standard'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center gap-3 p-3.5 bg-white/5 rounded-2xl border border-white/5 min-w-0">
                          <span className="text-sm font-bold text-gray-400 shrink-0">Bond (AUD)</span>
                          <span className="text-xs md:text-sm font-black text-emerald-400 bg-emerald-500/15 border border-emerald-500/20 px-3.5 py-1.5 rounded-xl truncate text-right max-w-[60%]">
                            ${String(
                              formData.bond_exact_amount ||
                                (formData.rent ? (parseFloat(String(formData.rent).replace(/[^0-9.]/g, '')) || 0) * 4 : 0)
                            ).replace(/^\$/, '')}
                          </span>
                        </div>
                        <div className="flex justify-between items-center gap-3 p-3.5 bg-white/5 rounded-2xl border border-white/5 min-w-0">
                          <span className="text-sm font-bold text-gray-400 shrink-0">Bathroom</span>
                          <span className="text-xs md:text-sm font-black text-white bg-white/10 border border-white/10 px-3.5 py-1.5 rounded-xl truncate text-right max-w-[60%]">
                            {formData.bathroomAccess ? `${formData.bathroomAccess} Access` : 'Shared Access'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center gap-3 p-3.5 bg-white/5 rounded-2xl border border-white/5 min-w-0">
                          <span className="text-sm font-bold text-gray-400 shrink-0">Availability</span>
                          <span className="text-xs md:text-sm font-black text-red-400 bg-red-500/20 border border-red-500/20 px-3.5 py-1.5 rounded-xl truncate text-right max-w-[60%]">
                            {formData.availableDate || 'Immediately'}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Attractive Animated Popup Modal for Photo Limit / 10MB Size / Publish Alerts */}
      <AnimatePresence>
        {popupAlert.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1050] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md"
            onClick={() => setPopupAlert(prev => ({ ...prev, open: false }))}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 16 }}
              transition={{ type: 'spring', stiffness: 380, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md bg-gradient-to-b from-[#1a1c26] to-[#11131a] border border-red-500/30 rounded-3xl p-6 sm:p-8 text-center shadow-[0_25px_70px_rgba(220,38,38,0.35)] overflow-hidden"
            >
              <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-52 h-32 bg-red-500/25 blur-3xl pointer-events-none" />

              <button
                type="button"
                onClick={() => setPopupAlert(prev => ({ ...prev, open: false }))}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 text-gray-400 hover:text-white flex items-center justify-center transition-colors"
              >
                <X size={16} />
              </button>

              <motion.div
                initial={{ rotate: -10, scale: 0.8 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 18 }}
                className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-amber-500 p-0.5 mx-auto mb-5 shadow-xl shadow-red-600/30"
              >
                <div className="w-full h-full bg-[#161822] rounded-[14px] flex items-center justify-center text-red-400">
                  <AlertCircle size={32} />
                </div>
              </motion.div>

              <h3 className="text-xl sm:text-2xl font-black text-white mb-2 tracking-tight">
                {popupAlert.title}
              </h3>

              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                {popupAlert.message}
              </p>

              {popupAlert.subtitle && (
                <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/25 text-red-300 text-xs font-bold px-4 py-2 rounded-full mb-6">
                  <Sparkles size={13} className="text-amber-400 shrink-0" />
                  <span>{popupAlert.subtitle}</span>
                </div>
              )}

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                type="button"
                onClick={() => setPopupAlert(prev => ({ ...prev, open: false }))}
                className="w-full py-3.5 px-6 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-red-600 via-red-500 to-amber-500 shadow-lg shadow-red-900/40 hover:shadow-red-600/40 transition-all"
              >
                Got It, Okay!
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer Navigation */}
      <footer className="relative z-20 w-full bg-[#0f1017]/95 backdrop-blur-2xl border-t border-white/10 py-3.5 sm:py-4 px-4 sm:px-6 md:px-10 shadow-[0_-10px_25px_rgba(0,0,0,0.5)]">
        <div className="max-w-2xl mx-auto w-full">
          {stepError && (
            <div className="mb-3 p-3 bg-red-500/15 border border-red-500/30 text-red-400 text-xs font-bold rounded-xl flex items-center gap-2 animate-pulse">
              <AlertCircle size={16} className="shrink-0 text-red-400" />
              <span>{stepError}</span>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 sm:gap-4">
            <button
              type="button"
              onClick={handlePrev}
              className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-bold text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 sm:py-3 px-4 sm:px-5 rounded-xl transition-all"
            >
              <ChevronLeft size={16} />
              Back
            </button>

            {currentStep < 6 ? (
              <motion.button
                type="button"
                whileTap={{ scale: 0.97 }}
                onClick={handleNext}
                disabled={currentStep === 1 && !formData.offerType}
                className={`group flex items-center gap-1.5 sm:gap-2 py-2.5 sm:py-3.5 px-6 sm:px-8 rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 ${
                  currentStep === 1 && !formData.offerType
                    ? 'bg-white/10 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow-xl shadow-red-900/40 hover:shadow-red-800/50 hover:-translate-y-0.5'
                }`}
              >
                Continue
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </motion.button>
            ) : (
              <motion.button
                type="button"
                whileTap={{ scale: 0.97 }}
                onClick={handlePublish}
                disabled={isSubmitting}
                className="flex items-center gap-1.5 sm:gap-2 py-2.5 sm:py-3.5 px-6 sm:px-10 rounded-xl text-xs sm:text-sm font-bold bg-gradient-to-r from-red-600 to-red-500 text-white shadow-xl shadow-red-900/40 hover:shadow-red-800/50 hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Publishing...' : 'Publish Listing'}
                <CheckCircle size={16} />
              </motion.button>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// REUSABLE COMPONENTS
// ---------------------------------------------------------------------------

function OfferCard({ selected, onSelect, icon, title, desc }) {
  return (
    <motion.label
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.99 }}
      onClick={onSelect}
      className={`block border-2 rounded-2xl p-3.5 sm:p-5 cursor-pointer transition-all duration-300 ${
        selected
          ? 'border-red-500 bg-red-500/10 shadow-lg shadow-red-900/30 ring-2 sm:ring-4 ring-red-500/10'
          : 'border-white/10 bg-white/[0.03] hover:border-red-500/40'
      }`}
    >
      <input type="radio" className="hidden" checked={selected} onChange={onSelect} />
      <div className="flex items-start gap-3 sm:gap-4">
        <div
          className={`p-2.5 sm:p-3.5 rounded-xl shrink-0 transition-colors ${
            selected ? 'bg-gradient-to-br from-red-500 to-red-700 text-white shadow-lg shadow-red-900/40' : 'bg-white/10 text-gray-300'
          }`}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`font-bold text-base sm:text-lg mb-0.5 sm:mb-1 ${selected ? 'text-white' : 'text-gray-100'}`}>{title}</h3>
          <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">{desc}</p>
        </div>
        <div
          className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 shrink-0 flex items-center justify-center transition-all duration-300 mt-0.5 ${
            selected ? 'border-red-500 bg-red-500' : 'border-white/20'
          }`}
        >
          {selected && <CheckCircle size={13} className="text-white" />}
        </div>
      </div>
    </motion.label>
  );
}

function SegmentedControl({ options, value, onChange }) {
  const id = useId();
  const layoutId = `segmented-${id}`;
  return (
    <div className="flex w-full border border-white/10 bg-white/5 rounded-xl p-1.5 gap-1">
      {options.map(opt => {
        const isSelected = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`relative flex-1 text-center py-2.5 text-xs sm:text-sm font-semibold cursor-pointer transition-colors duration-200 rounded-lg ${
              isSelected ? 'text-white' : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            {isSelected && (
              <motion.div
                layoutId={layoutId}
                className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-500 rounded-lg shadow-lg shadow-red-900/40"
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
            <span className="relative z-10">{opt}</span>
          </button>
        );
      })}
    </div>
  );
}
